// Geometry vocabulary: vertical = an x-stripe down the shelves, horizontal = a band across them.
// Which one a level is comes from `SpineLevel.orient`, never from the level's depth: grading a
// banded level on its left and right edges punishes a plan for doing what the merchant asked.
//
// `SpineLevel.orient` speaks the ENGINE's words, which run the other way: "horizontal"
// separates a level's values along x (a stripe, merchandising's vertical blocking) and
// "vertical" splits the shelves between them (a band, horizontal blocking) — see
// classifyMove.ts, spineInference.ts and buildLayout.ts. `blockingOf` is the one place the
// two vocabularies meet.
import {
  type FlagSeverity,
  type LengthUnit,
  type Orientation,
  type SpineLevel,
  type StudioDraft,
} from "./studioTypes";

export type BlockingAxis = "vertical" | "horizontal";

/** The blocking a studio orientation asks for: an x-separated level is a stripe (vertical
 *  blocking), a shelf-split level is a band (horizontal blocking). */
export const blockingOf = (orient: Orientation): BlockingAxis =>
  orient === "horizontal" ? "vertical" : "horizontal";

/** The studio orientation that asks for `blocking`: the inverse of `blockingOf`. */
export const orientOf = (blocking: BlockingAxis): Orientation =>
  blocking === "vertical" ? "horizontal" : "vertical";

/**
 * The score's own version. BUMP IT whenever a weight, a split, a default or a measure changes,
 * so two numbers are only ever compared when they were produced the same way.
 *
 * 1.x — blocking / cdt / fullness / ergonomics, every level above size graded as a stripe.
 * 2.0.0 — orientation is read, bands and range and rules-kept are graded, levels are weighted.
 * 2.1.0 — a value split into pieces is the worst thing a block can do. A stripe is one block
 *         only when its shelves line up into one column; a stripe that jumps sideways between
 *         shelves is two. A brand met twice along one shelf costs double. Each break costs on
 *         its own instead of being averaged away by the wall's footprint, contiguity carries
 *         most of blocking, and blocking carries 40 of the 100 points.
 * 2.1.1 — a per-scope orientation override reaches the level the studio keyed it to, not the
 *         level at that depth of the profile's order.
 * 2.1.2 — on a ragged wall, a brand's two pieces either side of a bay with no board on that row
 *         are two pieces, and a pack on a row its bay has no board for counts on the board below.
 * 3.1.0 — 90 and above means a plan can be approved without a person looking, so it needs every
 *         hard check passed: no overlap, nothing past an edge, no empty shelf, no brand dropped,
 *         at most `unplacedTolerance` of the range left off, every top-level block whole,
 *         every accepted band kept and the shelves full. A plan that fails one is scaled into
 *         the band below: its average × (approvalThreshold − 1) / 100.
 * 3.0.0 — the hierarchy is never inferred from the plan being graded, so no damage to a plan can
 *         flatter it. Blocking is block SHAPE: each block one piece that fills its rectangle,
 *         whichever way it runs. Children read in their ranked order inside a block. Blocking
 *         carries 55 points, and a check that grades nothing is dropped, never scored 1.
 * 3.2.0 — straight edges are back in blocking: half of it is the ragged-edge measure (each vertical
 *         block's left and right edges across its shelves, one pack forgiven, zero at four packs),
 *         half is block shape. Shape alone let a plan trade aligned columns away for order and
 *         ergonomics and score higher while looking worse.
 * 3.3.0 — 2D blocking when the contract's spine is given: horizontal levels graded as bands (each
 *         bay carries the band on the same shelves) and share the edges' half of blocking with the
 *         vertical levels, whose stripes are keyed by their full path. Without a spine, 3.2.0.
 * 3.4.0 — ergonomics weighs 1 of the 100 instead of 5: a band rule can pin heavy packs high, so
 *         heavy-low is sometimes impossible by design and must not decide a plan's fate.
 * 3.4.1 — a stripe runs the height of its section: in a section on several shelves, a brand that
 *         holds only some of them (Downy sheets on one sheet shelf, Gain on the other) breaks its
 *         column, priced like a piece of block shape and taken with the ragged edges.
 * 3.4.2 — a catch-all brand ("Others", "Unspecified", "Unassigned …") is no brand: it stands no
 *         column, breaks none, and is never a brand eliminated.
 * 3.5.0 — a store pog's bands are the restrictions its solve was SENT: the generation request's
 *         per-shelf `variants_restrictions` (or the CDT emission stored beside it), not the
 *         fences stored on the pog, which can be stale category-wide groups the generation never
 *         carried. Those stored fences are read only when nothing else exists, and the result
 *         says so (`bandSource: "legacy fences"`). A restriction names a variant (CDT0) value
 *         and governs exactly the products whose variant it is: no brand or other column is
 *         read, so "Old Spice" no longer governs "Old Spice Premium".
 * 3.6.0 — not every product must be placed, only the store's core range (Wendy, 2026-10-01). The
 *         unplaced check counts the core-range products left off, against `unplacedTolerance` of
 *         the core range: the request's `min_distribution` store and core range items, else the
 *         products the pog flags `inCoreRange` (`coreRangeSource` says which). An empty core range
 *         grades nothing. The range component is unchanged.
 * 3.6.1 — fix to 3.5.0: a sent MUST_HAVE whose variant was left entirely unplaced is graded as
 *         missing, not dropped as not graded.
 * 3.7.0 — one scorer: the Studio copy's 3.5.0–3.6.1 (graded in the editor tile) and this copy's
 *         3.4.1–3.4.2 (stored) were two branches of 3.4.0; this is both, plus three fixes the
 *         stored copy carried unversioned: a MUST_HAVE shelf named in several bays is checked in
 *         each bay, MUST_HAVE_ONLY admits no other product in its cells, and fullness counts only
 *         a shelf's merchandisable strips (a riser or a pole's dead span is not empty shelf).
 * 3.7.1 — fixes to 3.7.0: MUST_HAVE_ONLY rules on the same cell are one allow-list ("only the
 *         listed variants"), so two of them no longer breach each other; a zero-width riser offers
 *         no live strip even when dead spans are stored beside it; "N-A" names no block, as "N/A"
 *         and "NA" already did.
 * 3.8.0 — the Studio's DEV-3432 review fixes (3.5.0–3.5.3 on its own copy) under the one scorer:
 *         orientation is read in the studio's words and per scope (`orientOverrides`), so a scope
 *         the merchant banded is graded as a band, its brands standing no column; per-scope
 *         overrides are looked up by the scope the Studio keyed them to; band shelves are counted
 *         on each bay's own boards, and on a ragged wall a value either side of a bay with no
 *         board on that row is two pieces; derived heavy packs are strictly the heaviest; only
 *         fencing bands excuse a split; a tier ladder no placed pack carries a tier of is not
 *         graded, and the stripes keep the whole edges part when no band graded anything.
 * 3.10.0 — shelves-not-full also fails when any shelf row window holds more than
 *         1 − `approvalFullness` air, and names those rows. (3.9.0 is the horizontal-bands grading.)
 * 3.14.0 — fixtures that hold no product never move the score: a riser, a non-merchandisable
 *         board or one obstructed end to end is no shelf row (never empty, never a low shelf,
 *         never a gap between band shelves), and a pole's strip parts no block, on its shelf or
 *         in its rectangle.
 * 3.15.0 — a size tier block naming no sub-brand (`isSizeOnlyTier`) never fails the
 *         block-in-pieces gate.
 * 3.16.0 — fullness counts the height left under the board above (DEV-3730): air taller than
 *         one more pack, as a share of the opening, lowers fullness (horizontal × vertical); the
 *         top shelf and hang cells are not graded, and the shelves-not-full gate stays on width.
 *         A `-UNIT` twin counts as its tray's range item in the unranged check (DEV-3531),
 *         folded only when the request pairs them in `either_or_items`.
 *         A top-level block in one piece whose outline (each row from its first pack to its last)
 *         fills less than `APPROVAL_BLOCK_FILL` (60%) of its rectangle, a Z or an L, fails the
 *         new block-out-of-shape gate (DEV-3517). Air between packs is fullness's, not shape's.
 *         A pog with no CDT release graded on its own columns follows the blocking its request
 *         sent (`blockingFromRequest`, DEV-3670), every level a band on a horizontal wall.
 *         A store pog whose release has no levels, not only one with no release, is graded the
 *         same way (DEV-3531), and `blockingFromRequest` also reads `horizontalRectangles`: on a
 *         horizontally blocked planogram every level is a band, graded on its top and bottom
 *         edges, not a stripe.
 *         Block-in-pieces skips a row whose board is dead at x, and a stretch's pack width is the
 *         placement's own pitch, not the SKU's first width (DEV-3531).
 * 3.17.0 — a top-level stripe that staircases is not approvable (block-staircased, DEV-3729).
 *         A staircase is one piece, so block-in-pieces never saw it, and its cost in shape and
 *         edges was averaged over every block on the wall (toilet paper S1533 scored 96 with three
 *         brands stepping down bays 1–3). A top-level stripe in one piece that fills less than
 *         `staircaseFill` (0.7) of its rectangle fails the gate. Bands are not read: a banded
 *         brand runs on from shelf to shelf like a line of text.
 *         A short vertical block with other short vertical blocks stacked under or over it on
 *         adjoining shelves is no broken column (Wendy, 2026-10-20/21). On each shelf it leaves,
 *         empty shelf under its width is no hole; another block's products inside the stack past
 *         `jitterPacks` packs are. With `stackSeamMayShift` (default true) the seam inside the
 *         stack off its edge, plus another block's products reaching in from its outside edge,
 *         are graded as one vertical edge off by that much; false breaks it past `jitterPacks`.
 * 3.18.0 — a rack standing on a host shelf: each rack board is a row for block shape and block
 *         order at its own height (in the wall row it lines up with, else its own) and its own
 *         pack width; a rack board's empty part is no overhang on the host below; fences, fullness
 *         and range still grade rack boards on their host's row, where a stocked rack is live
 *         shelf and a value is fenced only if every board of the cell fences it (sent and stored
 *         fences), while a MUST_HAVE on one board of the cell is kept only on that board.
 * 3.19.0 — unitless (DEV-3569): every length is the plan's raw units, never centimetres. The
 *         `gap` reason says raw / 100 in the category's `lengthUnit` (absent, inches), and the
 *         slack and pack floor are shares of the plan's own bay (a tenth of an inch and a
 *         centimetre on a 48 in bay). A score stored at 3.18.0 holds centimetres, so a reader
 *         grades the plan again.
 */
export const AESTHETICS_VERSION = "3.19.0";

/** Below this outline fill a brand reads as a Z or an L, a defect a shopper sees however small
 *  the brand; a plain wrap onto the next row stays above it. */
export const APPROVAL_BLOCK_FILL = 0.6;

export interface AestheticsWeights {
  blocking: number;
  cdt: number;
  fullness: number;
  ergonomics: number;
  /** The plan obeys the accepted bands. */
  rulesKept: number;
  /** Everything ranged is on the plan, and no brand quietly vanished. */
  range: number;
}

export interface TierLevel {
  label: string;
  values: readonly string[];
}

export interface AestheticsRules {
  brandAttr: string;
  tierAttr: string;
  sizeAttr: string;
  flavourAttr: string;
  /** Levels above size the merchant wants as an x-stripe: graded on left and right edges. */
  verticalAttrs: readonly string[];
  /** Levels above size the merchant wants as a band across shelves: graded on top and bottom. */
  horizontalAttrs: readonly string[];
  /** Every graded level in SPINE order. Level weight follows this depth, not the measure a
   *  level happens to land in, so a plan's top split always carries the most weight. */
  levelOrder: readonly string[];
  /**
   * Per-scope exceptions to a level's blocking, from `StudioDraft.orientOverrides`, already in
   * the score's vocabulary and keyed as the Studio keys them (see `walkSplitScopes`): the values
   * that split under that scope are graded on this axis instead of the level's own.
   */
  scopeBlocking?: Readonly<Record<string, BlockingAxis>>;
  /** Top shelf first; a tier is a level. Absent values are ungraded. */
  tierOrder: readonly TierLevel[];
  lastInBlock: readonly string[];
  bayOwners: readonly string[];
  /**
   * Pounds at which a pack counts as heavy. 0 does NOT stand the measure down: with no stated
   * threshold the heaviest `heavyShare` of the plan's own packs are the heavy ones, so the
   * defaults still grade something. A profile that knows its own number sets it.
   */
  bulkPounds: number;
  /** Share of packs treated as heavy when `bulkPounds` is 0. */
  heavyShare: number;
  lowerShelfShare: number;
  jitterPacks: number;
  /**
   * Short vertical blocks stacked on adjoining shelves fill the same width as a stack. True, a
   * seam inside the stack that sits off a short block's edge, plus another block's products
   * reaching in from its outside edge, are graded as a vertical edge off by that much
   * (`jitterPacks` forgiven, a full break `raggedZeroPacks` past that); false, either one past
   * `jitterPacks` packs is a full break.
   */
  stackSeamMayShift: boolean;
  raggedZeroPacks: number;
  flagsPerHundredZero: number;
  /**
   * Weight of level d is `levelWeightDecay ** d`: the top level carries 1, its child 0.6, the
   * next 0.36 and so on. Top heavy, deep light — a shopper meets the top split first, and a
   * broken flavour ribbon three levels down is not a broken brand block.
   */
  levelWeightDecay: number;
  /**
   * How hard one broken block hits contiguity. A value in `n` columns adds `n - 1` break units,
   * a value met `m` times along one shelf adds `(m - 1) × contiguityInterleaveFactor` more, each
   * unit weighted by the level's weight, and the component scores `1 - cost × penalty`, floored
   * at 0. At 0.5, two brands that swapped sides (two columns each) score 0.4 and one brand
   * scattered inside another's stripe scores 0.1, whatever else is on the wall: a shopper who
   * meets a brand twice does not care how many other brands were tidy. The merchant's rulings
   * live here: a swap is far below a clean plan, and a scatter is far below a swap.
   */
  contiguityBreakCost: number;
  /** A brand met twice along ONE shelf (another brand between its pieces) counts this many
   *  times a plain column break. The shopper walking the shelf is the one who sees it. */
  contiguityInterleaveFactor: number;
  /** Damage per break of a block, a break scaled by the square root of the share of the block
   *  it displaces. An extra piece is one break; an extra run along a shelf is
   *  `contiguityInterleaveFactor` breaks. At 1, a brand torn in half costs its block about 0.7,
   *  and two facings of a sub-brand inside a fifty-facing stripe cost it about 0.4. */
  blockPieceCost: number;
  /** Quality a block piece keeps when it fills none of its bounding rectangle. */
  blockFillFloor: number;
  /** The score at which a plan can be approved unseen. A plan failing any hard check is scaled
   *  into the band below it, whatever its average. */
  approvalThreshold: number;
  /** Share of the ranged SKUs a plan may leave off and still be approvable. */
  unplacedTolerance: number;
  /** Share of its bounding rectangle a top-level stripe must fill to be approvable: below it the
   *  stripe staircases across its shelves. */
  staircaseFill: number;
  /** How many levels from the top must have every block whole for a plan to be approvable. */
  approvalLevels: number;
  /** Fullness a plan needs to be approvable. */
  approvalFullness: number;
  /** The unit the category's raw lengths are hundredths of, for the words only: every measure
   *  reads the plan's raw numbers whatever this says. Absent, inches. */
  lengthUnit?: LengthUnit;
  severityPenalty: Readonly<Record<FlagSeverity, number>>;
  weights: AestheticsWeights;
  /** Parts of the blocking component. A part that grades nothing is renormalised away. */
  blockingSplit: { shape: number; edges: number; ladder: number };
  cdtSplit: { detector: number; ordering: number };
  /** Parts of the shelf-sense component. */
  shelfSenseSplit: { heavyLow: number; bigUnderSmall: number };
  /** Parts of the range component. */
  rangeSplit: { skusPlaced: number; brandsKept: number };
}

/** The rules as a profile stores them. Absent, the band list and the level order are the spine's
 *  to decide. A profile saved on schema 1.0 is brought to this shape by `migrateStudioProfile`. */
export type SavedAestheticsRules = Omit<AestheticsRules, "horizontalAttrs" | "levelOrder"> &
  Partial<Pick<AestheticsRules, "horizontalAttrs" | "levelOrder">>;

/**
 * The 100 points. Every one of them can be lost: no component is handed a free 1.0, and a
 * component that grades nothing on a given plan is renormalised away rather than scoring full
 * marks. Bump `AESTHETICS_VERSION` with any change here.
 *
 *   blocking  57 — every block one piece that fills its rectangle. What the eye sees first.
 *   cdt       16 — children in their ranked order inside a block, sizes and tiers in order.
 *   fullness  16 — full shelves, no holes, nothing past the shelf end.
 *   rulesKept  5 — the accepted bands are obeyed.
 *   ergonomics 1 — big packs under small ones, heavy packs low. Sometimes impossible by design
 *                  (a band rule pins the heavy packs high), so it barely weighs (Wendy, 2026-09-26).
 *   range      5 — everything ranged is placed, no brand dropped.
 */
export const DEFAULT_WEIGHTS: AestheticsWeights = {
  blocking: 57,
  cdt: 16,
  fullness: 16,
  rulesKept: 5,
  ergonomics: 1,
  range: 5,
};

export const DEFAULT_SEVERITY_PENALTY: Record<FlagSeverity, number> = {
  blocking: 1,
  toCheck: 0.4,
  toReview: 0.15,
};

const BASE_RULES: Omit<
  AestheticsRules,
  | "brandAttr"
  | "tierAttr"
  | "sizeAttr"
  | "flavourAttr"
  | "verticalAttrs"
  | "horizontalAttrs"
  | "levelOrder"
> = {
  tierOrder: [],
  lastInBlock: [],
  bayOwners: [],
  bulkPounds: 0,
  heavyShare: 0.25,
  lowerShelfShare: 0.5,
  jitterPacks: 1,
  // Wendy, 2026-10-20: a stack's seams may shift, capped as a vertical edge is.
  stackSeamMayShift: true,
  raggedZeroPacks: 4,
  flagsPerHundredZero: 20,
  levelWeightDecay: 0.6,
  contiguityBreakCost: 0.5,
  contiguityInterleaveFactor: 2,
  blockPieceCost: 1,
  blockFillFloor: 0.5,
  approvalThreshold: 90,
  unplacedTolerance: 0.02,
  approvalLevels: 2,
  staircaseFill: 0.7,
  approvalFullness: 0.95,
  severityPenalty: DEFAULT_SEVERITY_PENALTY,
  weights: DEFAULT_WEIGHTS,
  /* A configured tier ladder says something shape does not; with none, shape carries it alone. */
  blockingSplit: { shape: 0.5, edges: 0.5, ladder: 0.15 },
  cdtSplit: { detector: 0.6, ordering: 0.4 },
  shelfSenseSplit: { heavyLow: 0.5, bigUnderSmall: 0.5 },
  rangeSplit: { skusPlaced: 0.6, brandsKept: 0.4 },
};

const matches = (value: string, pattern: RegExp) => pattern.test(value);

/**
 * Merge a saved set of parts (weights summing to 100, a split summing to 1) over the defaults.
 *
 * A set given before some of its parts existed already sums to the whole on its own: a weight
 * set of 100 without rules-kept or range. Laying the new defaults on top of it would overshoot
 * the whole, so the score would be stamped with this version yet weigh its parts differently
 * from any profile saved today. The least surprising reading is that the merchant tuned the
 * parts they could see: those keep their own proportions inside the share the new parts leave,
 * and the new parts take exactly their default share. A set that does not sum to the whole is a
 * partial override and merges as given. A part the set no longer has is dropped.
 */
const mergeParts = <T extends object>(defaults: T, given: unknown, whole: number): T => {
  const base = defaults as Record<string, number>;
  const saved: Readonly<Record<string, unknown>> = isRecord(given) ? given : {};
  const keys = Object.keys(base);
  const savedOf = (key: string) => {
    const value = saved[key];
    return typeof value === "number" ? value : undefined;
  };
  const merged = Object.fromEntries(keys.map((key) => [key, savedOf(key) ?? base[key]]));
  const present = keys.filter((key) => savedOf(key) !== undefined);
  const missing = keys.filter((key) => savedOf(key) === undefined);
  const savedSum = present.reduce((sum, key) => sum + (savedOf(key) ?? 0), 0);
  if (
    present.length > 0 &&
    missing.length > 0 &&
    savedSum > 0 &&
    Math.abs(savedSum - whole) <= whole * 1e-6
  ) {
    const room = whole - missing.reduce((sum, key) => sum + base[key], 0);
    for (const key of present) merged[key] = ((savedOf(key) ?? 0) * room) / savedSum;
  }
  return merged as T;
};

const isRecord = (value: unknown): value is Readonly<Record<string, unknown>> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

type RuleParts = Pick<
  AestheticsRules,
  "weights" | "severityPenalty" | "blockingSplit" | "cdtSplit" | "shelfSenseSplit" | "rangeSplit"
>;

/** Each saved set of parts merged over `defaults` as `mergeParts` merges it. */
export const mergeRuleParts = (
  defaults: RuleParts,
  saved: Readonly<Record<string, unknown>>,
): RuleParts => ({
  weights: mergeParts(defaults.weights, saved.weights, 100),
  severityPenalty: {
    ...defaults.severityPenalty,
    ...(isRecord(saved.severityPenalty) ? saved.severityPenalty : {}),
  },
  blockingSplit: mergeParts(defaults.blockingSplit, saved.blockingSplit, 1),
  cdtSplit: mergeParts(defaults.cdtSplit, saved.cdtSplit, 1),
  shelfSenseSplit: mergeParts(defaults.shelfSenseSplit, saved.shelfSenseSplit, 1),
  rangeSplit: mergeParts(defaults.rangeSplit, saved.rangeSplit, 1),
});

/** Roles fill positionally around the size level so they match detectExceptions; overrides win. */
export const resolveAestheticsRules = (
  draft: Pick<StudioDraft, "spine"> & Partial<Pick<StudioDraft, "orientOverrides">>,
  overrides: Partial<SavedAestheticsRules> = {},
): AestheticsRules => {
  const attrs = draft.spine.map((level) => level.attr);
  const labels = draft.spine.map((level) => level.label ?? level.attr);
  const sizeIndex = (() => {
    const byAttr = attrs.findIndex((attr) => matches(attr, /size/i));
    if (byAttr >= 0) return byAttr;
    const byLabel = labels.findIndex((label) => matches(label, /size/i));
    return byLabel >= 0 ? byLabel : Math.max(0, attrs.length - 3);
  })();
  const sizeAttr = attrs[sizeIndex] ?? "size";
  // Empty when size is the top level so tier measures stand down instead of guessing.
  const tierAttr = sizeIndex > 0 ? attrs[sizeIndex - 1] : "";
  const brandAttr = attrs.length > 1 ? attrs[1] : (attrs[0] ?? "");
  const flavourAttr = attrs.length ? attrs[attrs.length - 1] : "";

  /* Above size, each level goes to the measure its own orientation asks for. A spine with no
     vertical level leaves `verticalAttrs` empty and the stripe measure stands down — which is
     the point: on a banded plan there are no stripes to be straight. The old positional slice
     is kept only as the fallback when every level declares itself horizontal AND nothing else
     would be graded, so a plan is never scored against nothing. */
  const above: readonly SpineLevel[] = draft.spine.slice(0, sizeIndex);
  const verticalAttrs = above
    .filter((level) => blockingOf(level.orient) === "vertical")
    .map((level) => level.attr);
  const horizontalAttrs = above
    .filter((level) => blockingOf(level.orient) === "horizontal")
    .map((level) => level.attr);
  const scopeBlocking = Object.fromEntries(
    Object.entries(draft.orientOverrides ?? {}).map(([scope, orient]) => [
      scope,
      blockingOf(orient),
    ]),
  );
  const fallback = attrs.slice(0, Math.max(1, sizeIndex));
  const resolvedHorizontal =
    verticalAttrs.length === 0 && horizontalAttrs.length === 0 ? fallback : horizontalAttrs;
  const merged = {
    brandAttr,
    tierAttr,
    sizeAttr,
    flavourAttr,
    verticalAttrs,
    horizontalAttrs: resolvedHorizontal,
    levelOrder: [] as readonly string[],
    scopeBlocking,
    ...BASE_RULES,
    ...overrides,
    ...mergeRuleParts(BASE_RULES, overrides),
  };

  /* A level belongs to ONE measure. A caller that names its stripes takes them out of the
     bands, so nothing is graded twice and no level is silently dropped. */
  const stripes = new Set(merged.verticalAttrs);
  const finalHorizontal = merged.horizontalAttrs.filter((attr) => !stripes.has(attr));
  const graded = [...merged.verticalAttrs, ...finalHorizontal];
  const inSpine = attrs.filter((attr) => graded.includes(attr));
  const outsideSpine = graded.filter((attr) => !attrs.includes(attr));

  return {
    ...merged,
    horizontalAttrs: finalHorizontal,
    levelOrder: overrides.levelOrder ?? [...inSpine, ...outsideSpine],
  };
};

/** Every level the score grades for blocking, in spine order, with its weight and its measure. */
export const gradedLevelsOf = (
  rules: AestheticsRules,
): readonly { attr: string; orient: BlockingAxis; weight: number; depth: number }[] => {
  const vertical = new Set(rules.verticalAttrs);
  const order = rules.levelOrder.length
    ? rules.levelOrder
    : [...rules.verticalAttrs, ...rules.horizontalAttrs];
  return order.map((attr, index) => ({
    attr,
    orient: vertical.has(attr) ? ("vertical" as const) : ("horizontal" as const),
    weight: rules.levelWeightDecay ** index,
    depth: index,
  }));
};

/** Per SKU, the scope each level splits it in, keyed as `scopeBlocking` is keyed. A level that
 *  never splits a SKU's scope has no entry: no override can reach it there. */
export type LevelScopes = ReadonlyMap<string, ReadonlyMap<string, string>>;

/** Whether a level is graded on `axis` anywhere: by its own blocking, or under a scope the
 *  merchant flipped. */
export const levelGradedOn = (
  rules: AestheticsRules,
  level: { attr: string; orient: BlockingAxis },
  axis: BlockingAxis,
  scopes: LevelScopes,
): boolean => {
  if (level.orient === axis) return true;
  const overrides = rules.scopeBlocking ?? {};
  for (const byAttr of scopes.values()) {
    const scope = byAttr.get(level.attr);
    if (scope !== undefined && overrides[scope] === axis) return true;
  }
  return false;
};

/** The axis a SKU's block of a level is graded on: its scope's override, else the level's own. */
export const blockingAt = (
  rules: AestheticsRules,
  level: { attr: string; orient: BlockingAxis },
  scopes: LevelScopes,
  skuId: string,
): BlockingAxis => {
  const scope = scopes.get(skuId)?.get(level.attr);
  return (scope !== undefined && rules.scopeBlocking?.[scope]) || level.orient;
};
